import { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber, formatPercent } from '../../styles/tokens'
import { Select } from '../../components/FormControls'
import { ErrorBanner, SkeletonRows, EmptyState } from '../../components/Feedback'

// =======================================================================
// Revision Comparison — Wave 4.3, the final Wave 4 component.
//
// STRICT RULE: every financial figure (Direct Cost, Overheads, Markup,
// Selling Price, Tender Price) is read verbatim from
// estimate_revision_calculation_chain / boq_item_selling_price. This page
// performs NO financial calculation — only deltas (B - A) and simple
// item_no-based matching, which are presentation logic, not business
// calculations.
//
// Matching key: item_no (never boq_item.id, which is regenerated per
// revision by clone_revision_estimating_content). Duplicate item_no
// within a single revision is treated as Ambiguous and excluded from
// automatic classification.
// =======================================================================

const STATUS_CONFIG = {
  added: { label: 'Added', bg: '#F0FDF4', border: '#86EFAC', text: '#166534' },
  removed: { label: 'Removed', bg: '#FEF2F2', border: '#FCA5A5', text: '#991B1B' },
  changed: { label: 'Changed', bg: '#FFF7ED', border: '#FDBA74', text: '#9A3412' },
  unchanged: { label: 'Unchanged', bg: '#F3F4F6', border: '#D1D5DB', text: '#6B7280' },
}

// P2: same proven pattern as TenderSummaryPage's P1 implementation --
// addition/subtraction over values freeze_estimate_revision already wrote
// verbatim at freeze time. No financial formula is recomputed here.
function toFinancialNumber(value, fieldPath) {
  if (value === undefined || value === null) return 0
  const n = Number(value)
  if (!Number.isFinite(n)) {
    throw new Error(`Frozen snapshot contains a non-numeric financial value at ${fieldPath}: ${JSON.stringify(value)}`)
  }
  return n
}

function buildFrozenChain(snap) {
  const boqData = Array.isArray(snap.full_boq_data) ? snap.full_boq_data : []
  const indirect = snap.full_indirect_data && typeof snap.full_indirect_data === 'object' ? snap.full_indirect_data : {}
  const pkgCosts = Array.isArray(indirect.package_specific_costs) ? indirect.package_specific_costs : []

  let boqSiteOverhead = 0, boqRisk = 0, boqItemMarkup = 0
  boqData.forEach((item, i) => {
    boqSiteOverhead += toFinancialNumber(item.boq_specific_site_overhead, `full_boq_data[${i}].boq_specific_site_overhead`)
    boqRisk += toFinancialNumber(item.boq_specific_risk, `full_boq_data[${i}].boq_specific_risk`)
    boqItemMarkup += toFinancialNumber(item.selling_price, `full_boq_data[${i}].selling_price`) - toFinancialNumber(item.cost_base, `full_boq_data[${i}].cost_base`)
  })

  let pkgSiteOverhead = 0, pkgRisk = 0, pkgMarkup = 0
  pkgCosts.forEach((pkg, i) => {
    pkgSiteOverhead += toFinancialNumber(pkg.package_specific_site_overhead, `package_specific_costs[${i}].package_specific_site_overhead`)
    pkgRisk += toFinancialNumber(pkg.package_specific_risk, `package_specific_costs[${i}].package_specific_risk`)
    pkgMarkup += toFinancialNumber(pkg.package_specific_cost_with_markup, `package_specific_costs[${i}].package_specific_cost_with_markup`)
      - toFinancialNumber(pkg.package_specific_cost, `package_specific_costs[${i}].package_specific_cost`)
  })

  return {
    direct_cost_total: toFinancialNumber(snap.direct_cost_total, 'direct_cost_total'),
    boq_specific_site_overhead_total: boqSiteOverhead,
    boq_specific_risk_total: boqRisk,
    package_specific_site_overhead_total: pkgSiteOverhead,
    package_specific_risk_total: pkgRisk,
    package_specific_markup_total: pkgMarkup,
    general_site_overhead_total: toFinancialNumber(indirect.general_site_overhead_total, 'full_indirect_data.general_site_overhead_total'),
    ho_overhead_total: toFinancialNumber(snap.ho_overhead_total, 'ho_overhead_total'),
    general_contingency_total: toFinancialNumber(indirect.general_contingency_total, 'full_indirect_data.general_contingency_total'),
    boq_item_markup_total: boqItemMarkup,
    markup_total: toFinancialNumber(snap.markup_total, 'markup_total'),
    tender_price_total: toFinancialNumber(snap.tender_price_total, 'tender_price_total'),
  }
}

// Loads one side's chain + BOQ items, choosing the live or frozen source
// based on that revision's own status -- each side is resolved
// independently so Draft-vs-Frozen and Frozen-vs-Frozen both work.
async function loadRevisionSide(revisionId, status) {
  if (status === 'frozen') {
    const snapRes = await supabase
      .from('freeze_snapshot')
      .select('direct_cost_total, ho_overhead_total, markup_total, tender_price_total, full_boq_data, full_indirect_data')
      .eq('estimate_revision_id', revisionId)
      .maybeSingle()
    if (snapRes.error) throw new Error(snapRes.error.message)
    if (!snapRes.data) throw new Error('This revision is marked frozen but no snapshot record could be found.')
    const chain = buildFrozenChain(snapRes.data)
    const items = (Array.isArray(snapRes.data.full_boq_data) ? snapRes.data.full_boq_data : []).map((it) => ({
      id: it.id, item_no: it.item_no, description: it.description, unit: it.unit,
      resolved_quantity: it.resolved_quantity, is_excluded: it.is_excluded,
      pricing: { direct_cost: it.direct_cost, selling_price: it.selling_price },
    }))
    return { chain, items }
  }

  const [chainRes, boqRes, pricingRes] = await Promise.all([
    supabase.from('estimate_revision_calculation_chain').select('*').eq('estimate_revision_id', revisionId).maybeSingle(),
    supabase.from('boq_item').select('id, item_no, description, unit, resolved_quantity, is_excluded').eq('estimate_revision_id', revisionId),
    supabase.from('boq_item_selling_price').select('boq_item_id, direct_cost, selling_price').eq('estimate_revision_id', revisionId),
  ])
  if (chainRes.error) throw new Error(chainRes.error.message)
  if (boqRes.error) throw new Error(boqRes.error.message)
  if (pricingRes.error) throw new Error(pricingRes.error.message)
  const pricingMap = Object.fromEntries((pricingRes.data || []).map((p) => [p.boq_item_id, p]))
  const items = (boqRes.data || []).map((it) => ({ ...it, pricing: pricingMap[it.id] }))
  return { chain: chainRes.data, items }
}

export default function RevisionComparisonPage() {
  const { tenderId } = useParams()
  const [revisions, setRevisions] = useState(null)
  const [revisionAId, setRevisionAId] = useState('')
  const [revisionBId, setRevisionBId] = useState('')
  const [chainA, setChainA] = useState(null)
  const [chainB, setChainB] = useState(null)
  const [itemsA, setItemsA] = useState(null)
  const [itemsB, setItemsB] = useState(null)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    supabase
      .from('estimate_revision')
      .select('id, revision_number, label, status')
      .eq('tender_id', tenderId)
      .order('revision_number')
      .then(({ data, error }) => {
        if (error) return setError(error.message)
        setRevisions(data)
        // Default to comparing the two most recent revisions, if available.
        if (data.length >= 2) {
          setRevisionAId(data[data.length - 2].id)
          setRevisionBId(data[data.length - 1].id)
        } else if (data.length === 1) {
          setRevisionAId(data[0].id)
        }
      })
  }, [tenderId])

  const loadComparison = useCallback(async () => {
    if (!revisionAId || !revisionBId || !revisions) return
    setError(null)

    const statusA = revisions.find((r) => r.id === revisionAId)?.status
    const statusB = revisions.find((r) => r.id === revisionBId)?.status

    try {
      const [sideA, sideB] = await Promise.all([
        loadRevisionSide(revisionAId, statusA),
        loadRevisionSide(revisionBId, statusB),
      ])
      setChainA(sideA.chain)
      setChainB(sideB.chain)
      setItemsA(sideA.items)
      setItemsB(sideB.items)
    } catch (e) {
      setError(e.message)
    }
  }, [revisionAId, revisionBId, revisions])

  useEffect(() => { loadComparison() }, [loadComparison])

  // -----------------------------------------------------------------
  // Matching: pure presentation logic (grouping + comparing already-
  // computed values), not a financial calculation.
  // -----------------------------------------------------------------
  const { matched, ambiguousItemNos } = useMemo(() => {
    if (!itemsA || !itemsB) return { matched: [], ambiguousItemNos: [] }

    const countA = {}
    const countB = {}
    for (const it of itemsA) countA[it.item_no] = (countA[it.item_no] || 0) + 1
    for (const it of itemsB) countB[it.item_no] = (countB[it.item_no] || 0) + 1

    const ambiguous = new Set([
      ...Object.keys(countA).filter((k) => countA[k] > 1),
      ...Object.keys(countB).filter((k) => countB[k] > 1),
    ])

    const mapA = new Map(itemsA.map((it) => [it.item_no, it]))
    const mapB = new Map(itemsB.map((it) => [it.item_no, it]))
    const allItemNos = new Set([...mapA.keys(), ...mapB.keys()])

    const rows = []
    for (const itemNo of allItemNos) {
      if (ambiguous.has(itemNo)) continue // handled separately

      const a = mapA.get(itemNo)
      const b = mapB.get(itemNo)

      let status
      let descriptionChanged = false

      if (a && !b) {
        status = 'removed'
      } else if (!a && b) {
        status = 'added'
      } else {
        const qtyChanged = Number(a.resolved_quantity) !== Number(b.resolved_quantity)
        const directCostChanged = Number(a.pricing?.direct_cost || 0) !== Number(b.pricing?.direct_cost || 0)
        const sellingPriceChanged = Number(a.pricing?.selling_price || 0) !== Number(b.pricing?.selling_price || 0)
        descriptionChanged = a.description !== b.description
        status = (qtyChanged || directCostChanged || sellingPriceChanged) ? 'changed' : 'unchanged'
      }

      rows.push({
        itemNo,
        status,
        descriptionChanged,
        a,
        b,
        description: b?.description || a?.description,
        unit: b?.unit || a?.unit,
      })
    }

    rows.sort((x, y) => x.itemNo.localeCompare(y.itemNo, undefined, { numeric: true }))
    return { matched: rows, ambiguousItemNos: [...ambiguous] }
  }, [itemsA, itemsB])

  const filteredRows = filter === 'all' ? matched : matched.filter((r) => r.status === filter)
  const counts = useMemo(() => {
    const c = { added: 0, removed: 0, changed: 0, unchanged: 0 }
    for (const r of matched) c[r.status]++
    return c
  }, [matched])

  if (error) return <ErrorBanner message={error} />
  if (revisions === null) return <SkeletonRows rows={3} cols={2} />

  const revisionAObj = revisions.find((r) => r.id === revisionAId)
  const revisionBObj = revisions.find((r) => r.id === revisionBId)
  const sameRevisionSelected = revisionAId && revisionBId && revisionAId === revisionBId

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px' }}>Compare Revisions</h1>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 20px' }}>
        Side-by-side comparison of two revisions of this tender — read directly from the existing calculation chain.
      </p>

      {revisions.length < 2 && (
        <div style={{ padding: 24 }}>
          <EmptyState title="At least two revisions are needed" description="This tender currently has fewer than two revisions to compare." />
        </div>
      )}

      {revisions.length >= 2 && (
        <>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
            <RevisionSelector label="Revision A" value={revisionAId} onChange={setRevisionAId} revisions={revisions} />
            <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 600 }}>vs</span>
            <RevisionSelector label="Revision B" value={revisionBId} onChange={setRevisionBId} revisions={revisions} />
          </div>

          {sameRevisionSelected && (
            <div style={{ padding: '10px 14px', background: colors.warningBg, color: colors.warningText, borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
              ⚠ Revision A and Revision B are the same — select two different revisions to compare.
            </div>
          )}

          {!sameRevisionSelected && chainA && chainB && (
            <>
              <SectionTitle>Financial Summary (Δ = B − A)</SectionTitle>
              <SummaryTable chainA={chainA} chainB={chainB} />

              <SectionTitle>BOQ-Level Changes</SectionTitle>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={matched.length} />
                <FilterChip active={filter === 'added'} onClick={() => setFilter('added')} label="Added" count={counts.added} cfg={STATUS_CONFIG.added} />
                <FilterChip active={filter === 'removed'} onClick={() => setFilter('removed')} label="Removed" count={counts.removed} cfg={STATUS_CONFIG.removed} />
                <FilterChip active={filter === 'changed'} onClick={() => setFilter('changed')} label="Changed" count={counts.changed} cfg={STATUS_CONFIG.changed} />
                <FilterChip active={filter === 'unchanged'} onClick={() => setFilter('unchanged')} label="Unchanged" count={counts.unchanged} cfg={STATUS_CONFIG.unchanged} />
              </div>

              <BoqChangeTable rows={filteredRows} />

              {ambiguousItemNos.length > 0 && (
                <>
                  <SectionTitle>⚠ Ambiguous Matches</SectionTitle>
                  <div style={{ padding: '12px 14px', background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 8, fontSize: 13, color: '#9A3412', marginBottom: 20 }}>
                    The following item numbers appear more than once within a single revision, so they cannot be matched
                    automatically: <strong>{ambiguousItemNos.join(', ')}</strong>. Review these manually in each revision's BOQ.
                  </div>
                </>
              )}
            </>
          )}

          {!sameRevisionSelected && (!chainA || !chainB) && (
            <div style={{ padding: 24 }}>
              <EmptyState title="No calculation data for one or both revisions" description="Add BOQ items and pricing to both revisions to compare them." />
            </div>
          )}
        </>
      )}

      <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 20 }}>
        All figures are read directly from <code>estimate_revision_calculation_chain</code> (Draft revisions) or the
        immutable <code>freeze_snapshot</code> (Frozen revisions) — no calculation is performed in this page. Matching uses
        BOQ item number, not internal IDs (which are unique per revision).
      </p>
    </div>
  )
}

function RevisionSelector({ label, value, onChange, revisions }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase' }}>{label}</span>
      <Select value={value} onChange={(e) => onChange(e.target.value)} style={{ width: 260 }}>
        <option value="" disabled>Select revision…</option>
        {revisions.map((r) => (
          <option key={r.id} value={r.id}>Rev {r.revision_number} — {r.label} ({r.status})</option>
        ))}
      </Select>
    </label>
  )
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10, marginTop: 8 }}>
      {children}
    </div>
  )
}

function DeltaCell({ a, b }) {
  const delta = Number(b) - Number(a)
  const deltaPct = Number(a) !== 0 ? (delta / Number(a)) * 100 : null
  const positive = delta > 0
  const negative = delta < 0
  return (
    <span style={{ fontWeight: 600, color: positive ? '#B91C1C' : negative ? '#166534' : colors.textMuted }}>
      {positive ? '+' : ''}{formatNumber(delta)} {deltaPct != null && `(${positive ? '+' : ''}${formatNumber(deltaPct)}%)`}
    </span>
  )
}

function SummaryTable({ chainA, chainB }) {
  const rows = [
    { label: 'Direct Cost', a: chainA.direct_cost_total, b: chainB.direct_cost_total },
    { label: 'Site Overhead', a: Number(chainA.general_site_overhead_total) + Number(chainA.boq_specific_site_overhead_total) + Number(chainA.package_specific_site_overhead_total), b: Number(chainB.general_site_overhead_total) + Number(chainB.boq_specific_site_overhead_total) + Number(chainB.package_specific_site_overhead_total) },
    { label: 'HO Overhead', a: chainA.ho_overhead_total, b: chainB.ho_overhead_total },
    { label: 'Contingency', a: Number(chainA.general_contingency_total) + Number(chainA.boq_specific_risk_total) + Number(chainA.package_specific_risk_total), b: Number(chainB.general_contingency_total) + Number(chainB.boq_specific_risk_total) + Number(chainB.package_specific_risk_total) },
    { label: 'Markup', a: chainA.markup_total, b: chainB.markup_total },
  ]

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
            <Th>Component</Th>
            <Th align="right">Revision A</Th>
            <Th align="right">Revision B</Th>
            <Th align="right">Δ (B − A)</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} style={{ borderBottom: `1px solid ${colors.border}` }}>
              <Td>{r.label}</Td>
              <Td align="right" muted>{formatNumber(r.a)}</Td>
              <Td align="right" muted>{formatNumber(r.b)}</Td>
              <Td align="right"><DeltaCell a={r.a} b={r.b} /></Td>
            </tr>
          ))}
          <tr style={{ background: '#F8FAFF' }}>
            <Td style={{ fontWeight: 800, fontSize: 15 }}>Tender Price</Td>
            <Td align="right" style={{ fontWeight: 800, fontSize: 15, color: colors.primary }}>{formatNumber(chainA.tender_price_total)}</Td>
            <Td align="right" style={{ fontWeight: 800, fontSize: 15, color: colors.primary }}>{formatNumber(chainB.tender_price_total)}</Td>
            <Td align="right" style={{ fontWeight: 800, fontSize: 15 }}><DeltaCell a={chainA.tender_price_total} b={chainB.tender_price_total} /></Td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function FilterChip({ active, onClick, label, count, cfg }) {
  const colorsUsed = cfg || { bg: '#EEF2FF', border: colors.primary, text: colors.primary }
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
        border: `1.5px solid ${active ? colorsUsed.border : colors.border}`,
        background: active ? colorsUsed.bg : '#fff',
        opacity: active ? 1 : 0.6,
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 600, color: colorsUsed.text }}>{label}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color: colorsUsed.text, background: '#fff', borderRadius: 10, padding: '0 6px' }}>{count}</span>
    </button>
  )
}

function BoqChangeTable({ rows }) {
  if (rows.length === 0) {
    return (
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24, marginBottom: 20 }}>
        <EmptyState title="No items match this filter" />
      </div>
    )
  }
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
            <Th>Item No.</Th>
            <Th>Description</Th>
            <Th>Unit</Th>
            <Th align="right">Qty A → B</Th>
            <Th align="right">Selling Price A → B</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const cfg = STATUS_CONFIG[r.status]
            return (
              <tr key={r.itemNo} style={{ borderBottom: `1px solid ${colors.border}` }}>
                <Td muted>{r.itemNo}</Td>
                <Td>
                  {r.description}
                  {r.descriptionChanged && (
                    <div style={{ fontSize: 10, color: '#9A3412', marginTop: 2 }}>⚠ Description changed — verify same scope</div>
                  )}
                </Td>
                <Td muted>{r.unit}</Td>
                <Td align="right">
                  {r.a ? formatNumber(r.a.resolved_quantity) : '—'} → {r.b ? formatNumber(r.b.resolved_quantity) : '—'}
                </Td>
                <Td align="right">
                  {r.a ? formatNumber(r.a.pricing?.selling_price) : '—'} → {r.b ? formatNumber(r.b.pricing?.selling_price) : '—'}
                </Td>
                <Td>
                  <span style={{
                    display: 'inline-flex', padding: '3px 9px', borderRadius: 12,
                    background: cfg.bg, border: `1px solid ${cfg.border}`, fontSize: 11, fontWeight: 700, color: cfg.text,
                  }}>
                    {cfg.label}
                  </span>
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</th>
}
function Td({ children, align = 'left', muted, style }) {
  return <td style={{ textAlign: align, padding: '11px 16px', color: muted ? colors.textMuted : colors.textPrimary, ...style }}>{children}</td>
}
